import type { CitationOutput } from "@/components/tool-kit/source-citation-builder";
import {
  apaAuthors,
  apaInTextAuthors,
  type CitationParts,
  chicagoBibliographyAuthors,
  chicagoNoteAuthors,
  chicagoShortAuthors,
  mlaAuthors,
  mlaInTextAuthors,
  type PartialDate,
  type PersonName,
  parsePartialDate,
  tidyParts,
} from "@/lib/tool-kit/citation-parts";
import {
  apaMeetingDates,
  chicagoMeetingDates,
  mlaMeetingDates,
  quotedTitle,
  shortNoteTitle,
  titleCase,
} from "@/lib/tool-kit/citation-sources-more/common";
import {
  capitalizeSubtitle,
  closer,
  get,
  italic,
  mlaUrl,
  plain,
} from "@/lib/tool-kit/legal-sources/common";
import {
  beforeSubtitle,
  fullUrl,
  type MediaLabels,
  type MediaValues,
  parsePeople,
} from "@/lib/tool-kit/media-sources/common";

/*
 * A poster presented at a conference, in APA 7, MLA 9 and Chicago 18.
 *
 * Sources checked (October 2026):
 * - APA 7: the conference-presentation format on the APA Style site (blocked to
 *   automated readers; read through library guides that reproduce it — Augusta
 *   University, University of Lincoln): "Presenter, A. A. (Year, Month Day–Day). Title
 *   of poster in sentence case [Poster presentation]. Name of Conference, City, State,
 *   Country. URL". The date is the whole run of the conference, not the day of the
 *   session; the title is italic; the conference name is plain, in title case; with no
 *   DOI, link to the poster itself, and failing that to the program page. Lincoln's
 *   sample: "Fothergill, R., & Bromnick, R. (2016, April 26–28). Intentions to work
 *   with older adults: A critical exploration of psychology students' motivations and
 *   ambition [Poster presentation]. British Psychological Society Annual Conference,
 *   Nottingham. http://eprints.lincoln.ac.uk/id/eprint/24576/".
 * - MLA 9: the MLA Style Center's answer on a paper presented at a conference puts the
 *   title in quotation marks, the conference as the container (plain type) with the
 *   date, then the location — "Curnutt, Kirk. "A Meme-able Feast …" MLA Annual
 *   Convention, 7 Jan. 2021, online." A closing "Poster presentation." in the optional
 *   element is the practice of library guides (Kwantlen Polytechnic), not an MLA
 *   example.
 * - Chicago 18: §14.115 — a paper, slides or poster presented at a meeting is cited with
 *   the sponsorship, location and date after the title, no longer in parentheses, and
 *   normally in a note or the text rather than the bibliography. CMOS's sample is
 *   David G. Harper, "The Several Discoveries of the Ciliary Muscle," PowerPoint
 *   presentation, 25th Anniversary Meeting of the Cogan Ophthalmic History Society,
 *   Bethesda, MD, March 31, 2012. "Poster presented at" is the same pattern.
 */

export const POSTER_FIELDS = {
  authors: "authors",
  title: "title",
  conference: "conference",
  location: "location",
  start: "start",
  end: "end",
  url: "url",
  shortTitle: "shortTitle",
} as const;

const F = POSTER_FIELDS;

export interface PosterLabels extends MediaLabels {
  apaNote: string;
  mlaNote: string;
  chicagoNote: string;
}

interface Poster {
  authors: PersonName[];
  title: string;
  conference: string;
  location: string;
  start: PartialDate | null;
  end: PartialDate | null;
  url: string;
  shortTitle: string;
}

function readPoster(values: MediaValues): Poster {
  return {
    authors: parsePeople(get(values, F.authors)),
    title: capitalizeSubtitle(get(values, F.title)),
    conference: get(values, F.conference),
    location: get(values, F.location),
    start: parsePartialDate(get(values, F.start)),
    end: parsePartialDate(get(values, F.end)),
    url: fullUrl(get(values, F.url)),
    shortTitle: get(values, F.shortTitle),
  };
}

/* ---------------------------------------------------------------------- APA */

function apaReference(poster: Poster): CitationParts {
  const date = apaMeetingDates(poster.start, poster.end);
  const venue = [poster.conference, poster.location].filter(Boolean).join(", ");
  const tail = [venue ? `${venue}${closer(venue)}` : "", poster.url]
    .filter(Boolean)
    .join(" ");
  const label = ` [Poster presentation]. ${tail}`;
  if (poster.authors.length === 0) {
    return tidyParts([
      italic(poster.title),
      plain(` [Poster presentation]. (${date}). ${tail}`),
    ]);
  }
  const authors = apaAuthors(poster.authors);
  return tidyParts([
    plain(`${authors}${closer(authors)} (${date}). `),
    italic(poster.title),
    plain(label),
  ]);
}

function apaOutput(poster: Poster, labels: PosterLabels): CitationOutput {
  const year = poster.start ? String(poster.start.year) : "n.d.";
  const has = poster.authors.length > 0;
  const short = beforeSubtitle(titleCase(poster.title));
  return {
    key: "apa",
    label: labels.apa,
    entries: [
      { label: labels.referenceList, parts: apaReference(poster) },
      {
        label: labels.parenthetical,
        parts: has
          ? [plain(`(${apaInTextAuthors(poster.authors)}, ${year})`)]
          : [plain("("), italic(short), plain(`, ${year})`)],
      },
      {
        label: labels.narrative,
        parts: has
          ? [plain(`${apaInTextAuthors(poster.authors, true)} (${year})`)]
          : [italic(short), plain(` (${year})`)],
      },
    ],
    note: labels.apaNote,
  };
}

/* ---------------------------------------------------------------------- MLA */

function mlaWorksCited(poster: Poster): CitationParts {
  const title = titleCase(poster.title);
  const elements = [
    poster.conference,
    mlaMeetingDates(poster.start, poster.end),
    poster.location,
    poster.url ? mlaUrl(poster.url) : "",
  ].filter(Boolean);
  const parts: CitationParts = [];
  if (poster.authors.length > 0) {
    const authors = mlaAuthors(poster.authors);
    parts.push(plain(`${authors}${closer(authors)} `));
  }
  const venue = elements.length > 0 ? ` ${elements.join(", ")}.` : "";
  parts.push(plain(`${quotedTitle(title, ".")}${venue} Poster presentation.`));
  return tidyParts(parts);
}

function mlaOutput(poster: Poster, labels: PosterLabels): CitationOutput {
  const who =
    poster.authors.length > 0
      ? mlaInTextAuthors(poster.authors)
      : quotedTitle(beforeSubtitle(titleCase(poster.title)), "");
  return {
    key: "mla",
    label: labels.mla,
    entries: [
      { label: labels.worksCited, parts: mlaWorksCited(poster) },
      { label: labels.inText, parts: [plain(`(${who})`)] },
    ],
    note: labels.mlaNote,
  };
}

/* ------------------------------------------------------------------ Chicago */

/** "British Psychological Society Annual Conference, Nottingham, April 26–28, 2016" */
function chicagoVenue(poster: Poster): string {
  return [
    poster.conference,
    poster.location,
    chicagoMeetingDates(poster.start, poster.end),
  ]
    .filter(Boolean)
    .join(", ");
}

/** "poster presented at Conference, City, Date" — or just "poster, Date" with no meeting. */
function posterDescription(
  poster: Poster,
  venue: string,
  word: string
): string {
  if (poster.conference) {
    return `${word} presented at ${venue}`;
  }
  return venue ? `${word}, ${venue}` : word;
}

function chicagoNote(poster: Poster, title: string): CitationParts {
  const venue = chicagoVenue(poster);
  const description = posterDescription(poster, venue, "poster");
  const lead =
    poster.authors.length > 0 ? `${chicagoNoteAuthors(poster.authors)}, ` : "";
  const tail = poster.url
    ? `${description}, ${poster.url}.`
    : `${description}.`;
  return [plain(`${lead}${quotedTitle(title, ",")} ${tail}`)];
}

function chicagoShortNote(poster: Poster, title: string): CitationParts {
  const short = quotedTitle(poster.shortTitle || shortNoteTitle(title), ".");
  if (poster.authors.length === 0) {
    return [plain(short)];
  }
  return [plain(`${chicagoShortAuthors(poster.authors)}, ${short}`)];
}

function chicagoBibliography(poster: Poster, title: string): CitationParts {
  const sentences: string[] = [];
  if (poster.authors.length > 0) {
    const authors = chicagoBibliographyAuthors(poster.authors);
    sentences.push(`${authors}${closer(authors)}`);
  }
  sentences.push(quotedTitle(title, "."));
  const venue = chicagoVenue(poster);
  sentences.push(`${posterDescription(poster, venue, "Poster")}.`);
  if (poster.url) {
    sentences.push(`${poster.url}.`);
  }
  return [plain(sentences.join(" "))];
}

function chicagoOutput(poster: Poster, labels: PosterLabels): CitationOutput {
  const title = titleCase(poster.title);
  return {
    key: "chicago",
    label: labels.chicago,
    entries: [
      { label: labels.footnote, parts: chicagoNote(poster, title) },
      { label: labels.shortNote, parts: chicagoShortNote(poster, title) },
      { label: labels.bibliography, parts: chicagoBibliography(poster, title) },
    ],
    note: labels.chicagoNote,
  };
}

export function posterIsReady(values: MediaValues): boolean {
  return get(values, F.title) !== "";
}

export function formatPoster(
  values: MediaValues,
  labels: PosterLabels
): CitationOutput[] {
  const poster = readPoster(values);
  return [
    apaOutput(poster, labels),
    mlaOutput(poster, labels),
    chicagoOutput(poster, labels),
  ];
}
